import React, { useState } from "react";
import { toast } from "sonner";
import { Trash2, X } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/auth";

// Delete a ledger project added by accident or as a duplicate — logged usage is moved or deleted with it.
export default function DeleteProjectDialog({ project, projects, onClose, onDeleted }) {
  const others = projects.filter((p) => p.id !== project.id);
  const hasUsage = project.usage_count > 0;
  const [mode, setMode] = useState(others.length ? "move" : "delete");
  const [target, setTarget] = useState(others[0]?.id || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const q = hasUsage ? (mode === "move" ? `?usage=move&target_id=${encodeURIComponent(target)}` : "?usage=delete") : "";
      const { data } = await apiClient.delete(`/admin/billing/projects/${project.id}${q}`);
      toast.success(data.moved ? `Deleted ${project.name} · moved ${data.moved} entries` : `Deleted ${project.name}`);
      onDeleted();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const radio = "flex items-start gap-2 p-3 border border-white/10 hover:border-nex-gold/40 cursor-pointer transition-colors";
  return (
    <div data-testid="billing-delete-project-dialog" className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
      <div className="card-industrial corner-tick w-full max-w-md p-6 relative">
        <button type="button" onClick={onClose} data-testid="billing-delete-project-cancel-x" aria-label="Close" className="absolute top-3 right-3 text-nex-chrome hover:text-white"><X size={16} /></button>
        <div className="mono-label text-red-300">// Delete project</div>
        <h3 className="mt-1 text-xl font-semibold text-white">Delete “{project.name}”?</h3>
        {hasUsage ? (
          <div className="mt-4 space-y-2 text-sm">
            <p className="text-nex-chrome">This project has <span className="text-white font-semibold">{project.usage_count}</span> logged usage {project.usage_count === 1 ? "entry" : "entries"}.</p>
            {others.length > 0 && (
              <label className={radio}>
                <input type="radio" checked={mode === "move"} onChange={() => setMode("move")} data-testid="billing-delete-mode-move" className="mt-1" />
                <span className="flex-1">
                  <span className="text-white">Move usage into another project</span> <span className="text-nex-chrome">— best for duplicates</span>
                  <select value={target} onChange={(e) => setTarget(e.target.value)} disabled={mode !== "move"} data-testid="billing-delete-target"
                    className="input-industrial mt-2 w-full">
                    {others.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </span>
              </label>
            )}
            <label className={radio}>
              <input type="radio" checked={mode === "delete"} onChange={() => setMode("delete")} data-testid="billing-delete-mode-delete" className="mt-1" />
              <span><span className="text-white">Delete the usage too</span> <span className="text-nex-chrome">— best for accidents (credits return to the balance)</span></span>
            </label>
          </div>
        ) : (
          <p className="mt-3 text-sm text-nex-chrome">No usage is logged against it. This can’t be undone.</p>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="billing-delete-project-cancel" className="text-xs px-4 py-2 border border-white/10 text-nex-chrome hover:text-white font-mono uppercase tracking-[0.14em] transition">Cancel</button>
          <button type="button" onClick={submit} disabled={busy || (hasUsage && mode === "move" && !target)} data-testid="billing-delete-project-confirm"
            className="text-xs px-4 py-2 border border-red-400/60 bg-red-500/15 text-red-200 hover:bg-red-500/25 font-mono uppercase tracking-[0.14em] inline-flex items-center gap-1.5 transition disabled:opacity-50">
            <Trash2 size={12} /> {busy ? "Deleting…" : "Delete project"}
          </button>
        </div>
      </div>
    </div>
  );
}
